/**
 * Blood on the screen, for the three seconds a pane of glass costs.
 *
 * A 2D canvas over the frame rather than anything in it, and it is in `src/ui`
 * with the HUD for the same reason the HUD is: this is not something in the
 * world, it is something on the eye. Painting it here means it does not warp
 * with the acid layer, it does not have to be looked *at* to be seen, and the
 * whole effect costs one hidden element on every frame it is not up for -
 * which is all of them but three, once, in a run somebody has to earn.
 *
 * It is not a screen in the `refreshScreens()` sense and never asks to be one.
 * There is nothing to decide: `Game` hands it how long ago the window broke,
 * or `null`, and that is the entire interface. A restart is `null`.
 *
 * Every mark's arithmetic is src/render/Blood.ts, which knows nothing about a
 * canvas; this is the brush.
 */

import { BLOOD } from '../config/gameplay';
import {
  BLOOD_DURATION,
  sampleSplat,
  splatPose,
  splatSeeds,
  type SplatPose,
  type SplatSeed,
} from '../render/Blood';
import { mulberry32 } from '../utils/prng';
import { el, setHidden } from './dom';

/** Seeded once, at load. See `splatSeeds` for why it may never be reseeded. */
const SPLATTER_SEED = 0xb100d;

/** How many segments a mark's outline is drawn with. */
const OUTLINE_STEPS = 24;

function rgba(hex: number, alpha: number): string {
  return `rgba(${(hex >> 16) & 255}, ${(hex >> 8) & 255}, ${hex & 255}, ${alpha})`;
}

const CORE = rgba(BLOOD.core, 1);
const EDGE = rgba(BLOOD.edge, 1);

/** A mark's outline, in its own frame: long the way it was going, and pointed. */
function outlineRadius(seed: SplatSeed, theta: number): number {
  let wobble = 1;
  for (let lobe = 0; lobe < seed.lobes.length; lobe++) {
    // Two humps and up: one hump is a circle pushed sideways, which reads as a
    // mark that missed rather than as a mark with a shape.
    wobble += seed.lobes[lobe].amplitude * Math.sin((lobe + 2) * theta + seed.lobes[lobe].phase);
  }
  const along = Math.cos(theta);
  // Fat where it landed, drawn out and pointed where it was still going. The
  // power is what keeps the point a point instead of an egg.
  return wobble * (1 + seed.stretch * along) + seed.tail * Math.pow(Math.max(0, along), 6);
}

export class BloodOverlay {
  readonly root = el('canvas', 'blood');

  private readonly ctx = this.root.getContext('2d');
  private readonly seeds: SplatSeed[] = splatSeeds(mulberry32(SPLATTER_SEED));
  /** One pose, reused by every mark on every frame. See `sampleSplat`. */
  private readonly pose: SplatPose = splatPose();

  /** CSS pixels, and the backing store's ratio to them. */
  private width = 0;
  private height = 0;
  private ratio = 1;
  private showing = false;

  constructor() {
    setHidden(this.root, true);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
  }

  setSize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    // The same cap the renderer uses. Blood is soft enough that it would
    // survive a lower one, but two different ratios in one window is a way of
    // finding out that one of them was wrong on somebody else's display.
    this.ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.root.width = Math.max(1, Math.round(width * this.ratio));
    this.root.height = Math.max(1, Math.round(height * this.ratio));
    // `BLOOD.softness`, as a CSS filter on the element rather than a canvas
    // filter on the context. A context filter is not one blur, it is a blur
    // per fill - forty-odd passes a frame across the marks, trails and specks,
    // which was the difference between a splatter and a slideshow for the
    // three seconds it was up. The element's blur is one compositor pass over
    // the finished splatter, and it is also the blur the effect means: one
    // eye, one pane, one blur over the lot.
    this.root.style.filter = `blur(${(BLOOD.softness * height).toFixed(2)}px)`;
  }

  /**
   * `age` is seconds since the pane went, or null while there is still glass in
   * the frame - which is also what a restart hands over.
   */
  render(age: number | null): void {
    if (age === null || age < 0 || age >= BLOOD_DURATION) {
      if (this.showing) {
        this.showing = false;
        setHidden(this.root, true);
      }
      return;
    }
    if (!this.showing) {
      this.showing = true;
      setHidden(this.root, false);
    }
    this.paint(age);
  }

  private paint(age: number): void {
    const ctx = this.ctx;
    if (!ctx) return;

    ctx.setTransform(this.ratio, 0, 0, this.ratio, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);

    for (const seed of this.seeds) {
      const pose = sampleSplat(seed, age, this.pose);
      if (pose.opacity <= 0) continue;
      this.mark(ctx, seed, pose);
    }

    ctx.globalAlpha = 1;
  }

  private mark(ctx: CanvasRenderingContext2D, seed: SplatSeed, pose: SplatPose): void {
    const x = pose.x * this.width;
    const y = pose.y * this.height;
    const radius = pose.radius * this.height;
    const drip = pose.drip * this.height;
    ctx.globalAlpha = pose.opacity;

    // What it left behind coming down, drawn first so the drop sits on top of
    // its own trail. Flat and dark rather than wet: what is on the glass behind
    // a running drop is the thin end of it.
    if (drip > 1) {
      const half = radius * BLOOD.dripWidth;
      ctx.beginPath();
      ctx.moveTo(x - half, y);
      ctx.quadraticCurveTo(x - half * 0.3, y - drip * 0.5, x, y - drip);
      ctx.quadraticCurveTo(x + half * 0.3, y - drip * 0.5, x + half, y);
      ctx.closePath();
      ctx.fillStyle = EDGE;
      ctx.fill();
    }

    ctx.fillStyle = wet(ctx, x, y, radius);
    outline(ctx, x, y, radius, seed);
    ctx.fill();

    // The specks it threw off, out in front of it. Drawn round: at three or
    // four pixels across, a wobble is four pixels arguing with each other.
    for (const speck of seed.specks) {
      const speckRadius = Math.max(0.5, speck.radius * radius);
      const speckX = x + Math.cos(speck.angle) * speck.distance * radius;
      const speckY = y + Math.sin(speck.angle) * speck.distance * radius;
      ctx.fillStyle = wet(ctx, speckX, speckY, speckRadius);
      ctx.beginPath();
      ctx.arc(speckX, speckY, speckRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * A mark, as a fill: flat across the middle, darker from `rimFrom` outwards,
 * and rim colour everywhere past the radius - which is the point, the trail and
 * the specks. Deliberately almost no gradient at all. Light a mark from a point
 * and fourteen of them read as beads of jam stuck to the screen.
 *
 * Rebuilt per mark per frame, which is fourteen gradients on the frames this
 * runs at all and none on any of the others.
 */
function wet(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number): CanvasGradient {
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, CORE);
  gradient.addColorStop(BLOOD.rimFrom, CORE);
  gradient.addColorStop(1, EDGE);
  return gradient;
}

/** A mark's outline, walked round and turned to face the way it was thrown. */
function outline(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  seed: SplatSeed,
): void {
  ctx.beginPath();
  for (let step = 0; step <= OUTLINE_STEPS; step++) {
    const theta = (step / OUTLINE_STEPS) * Math.PI * 2;
    const r = radius * outlineRadius(seed, theta);
    const px = x + Math.cos(theta + seed.heading) * r;
    const py = y + Math.sin(theta + seed.heading) * r;
    if (step === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}
